import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import type { Rec } from '../../../../api/visitorsAgent'
import { agentApi } from '../../../../api/visitorsAgent'
import { btn, btnPrimary, card, ErrorLine } from '../../../../campaigns-desk/ui'
import { errorDetail } from '../../../../lib/visitorsFormat'
import { goalLabel, groupLabel, handoffButton, safeDeskHref } from '../../../../lib/visitorsAgent'
import { Md } from './Md'

const chip = 'inline-flex rounded-full bg-ink-600 px-2.5 py-0.5 text-[12px] font-semibold text-paper-dim ring-1 ring-ink-400'

export function RecommendationCard({ rec, onDiscuss, onChanged }: { rec: Rec; onDiscuss: (r: Rec) => void; onChanged: () => void }) {
  const [sent, setSent] = useState(false)
  const send = useMutation({ mutationFn: () => agentApi.handoff(rec.id), onSuccess: () => { setSent(true); onChanged() } })
  const shown = handoffButton(rec, sent)
  const sentHref = send.data?.href ?? rec.handoff_href ?? null
  const href = safeInternal(sentHref, rec.handoff_kind)
  return (
    <article className={`${card} space-y-3`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={chip}>{goalLabel(rec.goal)}</span>
        <span className={chip}>{groupLabel(rec.group)}</span>
        {rec.confidence && <span className="text-[12px] text-paper-faint">Confidence: {rec.confidence}</span>}
      </div>
      <h3 className="font-serif text-[18px] font-medium text-paper">{rec.title}</h3>
      <Md text={rec.body} />
      {rec.evidence.length > 0 && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
          {rec.evidence.map((e, i) => {
            const h = safeDeskHref(e.href)
            return <li key={i}>{h ? <Link to={h} className="text-amber-glow underline underline-offset-2">{e.label}</Link> : <span className="text-paper-dim">{e.label}</span>}</li>
          })}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {shown && (shown.disabled
          ? (href
            ? <Link to={href} className="text-[13px] font-semibold text-amber-glow underline underline-offset-2">{shown.label}</Link>
            : <span className="text-[13px] font-semibold text-paper-dim">{shown.label}</span>)
          : <button type="button" className={btnPrimary} disabled={send.isPending} onClick={() => { if (!send.isPending) send.mutate() }}>{send.isPending ? 'Sending…' : shown.label}</button>)}
        <button type="button" className={btn} onClick={() => onDiscuss(rec)}>Discuss</button>
      </div>
      <ErrorLine text={send.isError ? errorDetail(send.error) : null} />
    </article>
  )
}

/** The hand-off link goes to the other desk (an in-app path or a start link), so allow same-origin paths only. */
function safeInternal(href: string | null, kind: string | null): string | null {
  if (!href || !kind) return null
  return href.startsWith('/') && !href.startsWith('//') && !/[\s\\]/.test(href) ? href : null
}
